import { ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { formatDate } from '@angular/common';
import {
  MAT_NATIVE_DATE_FORMATS,
  MatDateFormats,
} from '@angular/material/core';
import { EMPTY_DATE_PLACEHOLDER, MAX_LOOKBACK_DAYS } from '@constants';

export const DIGICEM_DATE_FORMATS: MatDateFormats = {
  ...MAT_NATIVE_DATE_FORMATS,
  display: {
    ...MAT_NATIVE_DATE_FORMATS.display,
    dateInput: { day: '2-digit', month: '2-digit', year: '2-digit' },
  },
};

export const PERU_HOLIDAYS = [
  '2025-01-01', // Año Nuevo
  '2025-04-17', // Jueves Santo
  '2025-04-18', // Viernes Santo
  '2025-05-01', // Día del Trabajo
  '2025-06-07', // Batalla de Arica y Día de la Bandera
  '2025-06-29', // San Pedro y San Pablo
  '2025-07-23', // Día de la Fuerza Aérea del Perú
  '2025-07-28', // Fiestas Patrias
  '2025-07-29', // Fiestas Patrias
  '2025-08-06', // Batalla de Junín
  '2025-08-30', // Santa Rosa de Lima
  '2025-10-08', // Combate de Angamos
  '2025-11-01', // Día de Todos los Santos
  '2025-12-08', // Inmaculada Concepción
  '2025-12-09', // Batalla de Ayacucho
  '2025-12-25', // Navidad
  '2026-01-01', // Año Nuevo
];
export function toLocalIsoDateString(date: Date): string {
  const localDate = new Date(date);
  localDate.setHours(0, 0, 0, 0);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isHoliday(date: Date): boolean {
  if (isNaN(date.getTime())) return false;
  const iso = toLocalIsoDateString(date);
  return PERU_HOLIDAYS.includes(iso);
}

export function isBusinessDay(date: Date): boolean {
  if (isNaN(date.getTime())) return false;
  // Se esta comentando los domingos y feriados por petición del equipo de logística,
  // si se desea agregar esa validación, descomentar estas líneas
  //const day = date.getDay(); // 0 = Domingo, 6 = Sábado
  //return day !== 0 && !isHoliday(date);
  return true;
}

export function getBusinessDaysInRange(startDate: Date, endDate: Date): Date[] {
  if (
    isNaN(startDate.getTime()) ||
    isNaN(endDate.getTime()) ||
    startDate > endDate
  ) {
    return [];
  }
  const dates: Date[] = [];
  const current = new Date(startDate);
  current.setHours(0, 0, 0, 0);

  const final = new Date(endDate);
  final.setHours(0, 0, 0, 0);

  while (current <= final) {
    if (isBusinessDay(current)) {
      dates.push(new Date(current));
    }
    current.setDate(current.getDate() + 1);
  }

  return dates;
}

export function dateBusinessRangeValidator(
  startDate: Date,
  endDate: Date,
): ValidatorFn {
  const allowedDates = getBusinessDaysInRange(startDate, endDate).map(
    toLocalIsoDateString,
  );

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const parsed = parseLocalDate(value);
    if (!parsed) return { outOfRange: true };

    const date = new Date(parsed.year, parsed.month - 1, parsed.day);
    date.setHours(0, 0, 0, 0);

    const iso = toLocalIsoDateString(date);
    const isValid = allowedDates.includes(iso);

    return isValid ? null : { outOfRange: true };
  };
}

export function isValidDateFormat(dateStr: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
}

export function parseLocalDate(
  dateStr: string,
): { year: number; month: number; day: number } | null {
  if (!dateStr) return null;

  // Cortamos en 'T' si viene en formato ISO completo
  const [datePart] = dateStr.split('T');

  const [yearStr, monthStr, dayStr] = datePart.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if ([year, month, day].some(isNaN)) return null;

  const localDate = new Date(year, month - 1, day);

  const isValid =
    localDate.getFullYear() === year &&
    localDate.getMonth() === month - 1 &&
    localDate.getDate() === day;

  return isValid ? { year, month, day } : null;
}

export function formatDisplayDate(value: string | null | undefined): string {
  return value
    ? formatDate(value, 'dd/MM/yyyy', 'en-US')
    : EMPTY_DATE_PLACEHOLDER;
}

export function isDateRangeInverted(
  from: Date | null,
  to: Date | null,
): boolean {
  if (!from || !to) return false;
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return false;
  return from.getTime() > to.getTime();
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Combines two independent rules the ticket states separately: "antigüedad"
// (dateFrom must not reach further back than maxDays from today) and "span"
// (dateTo - dateFrom must not exceed maxDays). A span-only check would wrongly
// accept an old-but-narrow range (e.g. 2 years ago to 1.5 years ago).
export function isDateRangeOverMaxLookback(
  dateFrom: string,
  dateTo: string,
  maxDays = MAX_LOOKBACK_DAYS,
): boolean {
  const parsedFrom = parseLocalDate(dateFrom);
  const parsedTo = parseLocalDate(dateTo);
  if (!parsedFrom || !parsedTo) return false;

  const from = new Date(parsedFrom.year, parsedFrom.month - 1, parsedFrom.day);
  const to = new Date(parsedTo.year, parsedTo.month - 1, parsedTo.day);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const ageDays = Math.round((today.getTime() - from.getTime()) / MS_PER_DAY);
  const spanDays = Math.round((to.getTime() - from.getTime()) / MS_PER_DAY);

  return ageDays > maxDays || spanDays > maxDays;
}

// Widest `dateFrom` CA-03's own lookback cap allows, ending today. Used when
// some non-date filter is applied alone — without this, the effective export
// range would silently collapse to today only, narrower than what the
// applied filter (and the modal's count) actually cover.
export function maxLookbackStartDate(maxDays = MAX_LOOKBACK_DAYS): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - maxDays);
  return toLocalIsoDateString(date);
}
